import { getBlueprintEditorPath } from "../../../router/RouterUtils";
import {
  getBlueprintAdminEnvironmentLabel,
  normalizeMergeEnvironment,
} from "../../../environmentConfig";

export enum BlueprintAdminEnvironment {
  Develop = "DEVELOP",
  Local = "LOCAL",
  MuDevelop = "MU-DEVELOP",
  Production = "PRODUCTION",
  Staging = "STAGING",
}

type BlueprintVersionEnvironmentLink = {
  href: string;
  targetEnvironment: BlueprintLinkTargetEnvironment;
};

type BlueprintLinkTargetEnvironment =
  | BlueprintAdminEnvironment.Develop
  | BlueprintAdminEnvironment.Production;

const DEFAULT_DEVELOP_ADMIN_BASE_URL = "https://admin-develop.merge.dev";
const DEFAULT_PRODUCTION_ADMIN_BASE_URL = "https://admin.merge.dev";

const trimTrailingSlash = (value: string) => value.replace(/\/+$/, "");

const getBlueprintAdminEnvironmentFromLabel = (
  value: string | undefined
): BlueprintAdminEnvironment | undefined => {
  const environmentLabel = normalizeMergeEnvironment(value);

  if (environmentLabel === BlueprintAdminEnvironment.Develop) {
    return BlueprintAdminEnvironment.Develop;
  }

  if (environmentLabel === BlueprintAdminEnvironment.Local) {
    return BlueprintAdminEnvironment.Local;
  }

  if (environmentLabel === BlueprintAdminEnvironment.MuDevelop) {
    return BlueprintAdminEnvironment.MuDevelop;
  }

  if (environmentLabel === BlueprintAdminEnvironment.Production) {
    return BlueprintAdminEnvironment.Production;
  }

  if (environmentLabel === BlueprintAdminEnvironment.Staging) {
    return BlueprintAdminEnvironment.Staging;
  }

  return undefined;
};

export const getCurrentBlueprintAdminEnvironment = (): BlueprintAdminEnvironment | undefined =>
  getBlueprintAdminEnvironmentFromLabel(process.env.REACT_APP_ADMIN_ENV_LABEL) ||
  getBlueprintAdminEnvironmentFromLabel(getBlueprintAdminEnvironmentLabel()) ||
  getBlueprintAdminEnvironmentFromLabel(process.env.REACT_APP_MERGE_ENV);

export const isHighlightedBlueprintAdminEnvironment = (
  currentEnvironment = getCurrentBlueprintAdminEnvironment()
): boolean =>
  currentEnvironment === BlueprintAdminEnvironment.Develop ||
  currentEnvironment === BlueprintAdminEnvironment.MuDevelop ||
  currentEnvironment === BlueprintAdminEnvironment.Staging;

const getAdminBaseURLForEnvironment = (environment: BlueprintLinkTargetEnvironment): string => {
  if (environment === BlueprintAdminEnvironment.Production) {
    return trimTrailingSlash(
      process.env.REACT_APP_ADMIN_PRODUCTION_BASE_URL || DEFAULT_PRODUCTION_ADMIN_BASE_URL
    );
  }

  return trimTrailingSlash(
    process.env.REACT_APP_ADMIN_DEVELOP_BASE_URL || DEFAULT_DEVELOP_ADMIN_BASE_URL
  );
};

export const getCounterpartBlueprintAdminEnvironment = (
  currentEnvironment: BlueprintAdminEnvironment | undefined
): BlueprintLinkTargetEnvironment | undefined => {
  if (
    currentEnvironment === BlueprintAdminEnvironment.Develop ||
    currentEnvironment === BlueprintAdminEnvironment.MuDevelop ||
    currentEnvironment === BlueprintAdminEnvironment.Staging
  ) {
    return BlueprintAdminEnvironment.Production;
  }

  if (currentEnvironment === BlueprintAdminEnvironment.Production) {
    return BlueprintAdminEnvironment.Develop;
  }

  return undefined;
};

export const getBlueprintVersionEnvironmentLink = ({
  currentEnvironment = getCurrentBlueprintAdminEnvironment(),
  integrationID,
  blueprintVersionID,
}: {
  currentEnvironment?: BlueprintAdminEnvironment;
  integrationID: string;
  blueprintVersionID: string;
}): BlueprintVersionEnvironmentLink | undefined => {
  const targetEnvironment = getCounterpartBlueprintAdminEnvironment(currentEnvironment);

  if (!targetEnvironment) {
    return undefined;
  }

  return {
    href: `${getAdminBaseURLForEnvironment(targetEnvironment)}${getBlueprintEditorPath(
      integrationID,
      blueprintVersionID
    )}`,
    targetEnvironment,
  };
};
